// Crochets React de la zone terrain : état de la file (abonnement au synchroniseur partagé),
// lecture du travail et des opérations dans le stockage local (relus à chaque révision), état
// de connexion, réglages de l'appareil.
import type { MissionTerrain, OrdreMissionTerrain, TourneeTerrain } from '@cc/contrats';
import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { listerOperations, listerPhotos, lireMission, lireOrdre, lireTournee, lireTravail, type OperationLocale, type PhotoLocale, type TravailLocal } from './stockage';
import { synchroniseurPartage, type EtatSynchronisation } from './synchronisation';

function ecouterReseau(rappel: () => void): () => void {
  window.addEventListener('online', rappel);
  window.addEventListener('offline', rappel);
  return () => {
    window.removeEventListener('online', rappel);
    window.removeEventListener('offline', rappel);
  };
}

/** État du réseau lu dans navigator.onLine dès le premier rendu (une page ouverte hors connexion le sait aussitôt). */
export function useEnLigne(): boolean {
  return useSyncExternalStore(
    ecouterReseau,
    () => (typeof navigator === 'undefined' ? true : navigator.onLine !== false),
    () => true,
  );
}

/** État courant de la file de synchronisation, mis à jour par le synchroniseur partagé. */
export function useSynchronisation(): EtatSynchronisation {
  const s = synchroniseurPartage();
  return useSyncExternalStore(
    (rappel) => s.abonner(rappel),
    () => s.etat(),
    () => s.etat(),
  );
}

interface Lecture<T> {
  donnees: T | null;
  chargement: boolean;
  erreur: string | null;
  relire: () => void;
}

/** Lit une valeur du stockage local et la relit quand les données locales changent. */
function useLectureLocale<T>(lire: () => Promise<T | null>, cles: unknown[]): Lecture<T> {
  const { revision } = useSynchronisation();
  const [etat, setEtat] = useState<{ donnees: T | null; chargement: boolean; erreur: string | null }>({ donnees: null, chargement: true, erreur: null });
  const [tour, setTour] = useState(0);
  const relire = useCallback(() => setTour((t) => t + 1), []);
  useEffect(() => {
    let actif = true;
    lire()
      .then((d) => actif && setEtat({ donnees: d, chargement: false, erreur: null }))
      .catch((e: unknown) => actif && setEtat({ donnees: null, chargement: false, erreur: e instanceof Error ? e.message : 'Lecture locale impossible' }));
    return () => {
      actif = false;
    };
    // La fonction de lecture est recréée à chaque rendu ; ce sont les clés et la révision qui comptent.
  }, [revision, tour, ...cles]);
  return { ...etat, relire };
}

export function useTravailLocal(): Lecture<TravailLocal> {
  return useLectureLocale(lireTravail, []);
}

export function useMissionLocale(id: string | undefined): Lecture<MissionTerrain> {
  return useLectureLocale(() => (id ? lireMission(id) : Promise.resolve(null)), [id]);
}

export function useTourneeLocale(id: string | undefined): Lecture<TourneeTerrain> {
  return useLectureLocale(() => (id ? lireTournee(id) : Promise.resolve(null)), [id]);
}

export function useOrdreLocal(id: string | undefined): Lecture<OrdreMissionTerrain> {
  return useLectureLocale(() => (id ? lireOrdre(id) : Promise.resolve(null)), [id]);
}

export function useOperationsLocales(objetId?: string): Lecture<OperationLocale[]> {
  return useLectureLocale(() => listerOperations(objetId ? { objet_id: objetId } : {}), [objetId]);
}

export function usePhotosLocales(operationIdClient?: string): Lecture<PhotoLocale[]> {
  return useLectureLocale(() => listerPhotos(operationIdClient), [operationIdClient]);
}

/** Démarre le repli automatique de la file le temps que la zone terrain est montée. */
export function useRepliAutomatique(actif: boolean): void {
  useEffect(() => {
    if (!actif) return;
    const s = synchroniseurPartage();
    void s.rafraichirEtat();
    return s.demarrerAutomatique();
  }, [actif]);
}
